import type { PublicRequestableEvent } from '@/lib/rta-api.ts';

interface FindRequestableSelectionInput {
  eventId: number | string | null | undefined;
  selectedInventoryId: string | null | undefined;
  rows: readonly PublicRequestableEvent[];
}

/**
 * Return the server-authorized requestable rows for the active event.
 *
 * The rows are already scoped to the current production and gated by the
 * `publicRequestable()` server scope (the Nova `public_request_access_enabled`
 * + `request_to_attend_enabled` flags), so this only narrows to the selected
 * event and fails closed on ambiguous duplicate inventory ids — it never
 * hardcodes a production or an inventory label.
 */
export function getRequestableZonesForEvent(
  eventId: number | string | null | undefined,
  rows: readonly PublicRequestableEvent[]
): PublicRequestableEvent[] {
  if (eventId == null) return [];

  const activeEventId = String(eventId);
  const candidates = rows.filter(
    (row) => row.event_id === activeEventId && row.event_inventory_id.length > 0
  );

  const inventoryIds = new Set<string>();
  for (const row of candidates) {
    if (inventoryIds.has(row.event_inventory_id)) return [];
    inventoryIds.add(row.event_inventory_id);
  }

  return candidates;
}

/**
 * Resolve a widget selection to exactly one server-returned requestable row.
 * The canonical inventory id is the sole authority (the submit mutation
 * re-validates it against the same server scope), so a selection matches only
 * when its inventory id resolves to a single row of the active event.
 */
export function findRequestableSelection({
  eventId,
  selectedInventoryId,
  rows,
}: FindRequestableSelectionInput): PublicRequestableEvent | null {
  if (!selectedInventoryId) return null;

  const matches = getRequestableZonesForEvent(eventId, rows).filter(
    (row) => row.event_inventory_id === selectedInventoryId
  );

  return matches.length === 1 ? matches[0] : null;
}

/** Primary action rendered below event inventory selection. */
export type EventPrimaryAction = 'request-to-attend' | 'begin-booking' | 'none';

/**
 * Choose the event CTA without allowing a requestable row to fall through to
 * the paid booking path. A canonical RTA selection always replaces Begin
 * booking; otherwise a selected paid ticket retains the existing action.
 */
export function getEventPrimaryAction({
  activeRequestableSelection,
  hasPaidInventory,
}: {
  activeRequestableSelection: PublicRequestableEvent | null;
  hasPaidInventory: boolean;
}): EventPrimaryAction {
  if (activeRequestableSelection) return 'request-to-attend';
  if (hasPaidInventory) return 'begin-booking';
  return 'none';
}

/**
 * Coordinate async paid-ticket loads with the user's current paid/RTA intent.
 * A request token becomes invalid as soon as a newer load or an RTA selection
 * occurs, preventing late paid data from re-selecting inventory behind RTA.
 */
export class PaidTicketSelectionGuard {
  private revision = 0;
  private mode: 'paid' | 'requestable' = 'paid';

  /** Start a paid-ticket request and return its unique revision token. */
  beginPaidLoad(): number {
    this.revision += 1;
    return this.revision;
  }

  /** Make RTA authoritative and invalidate every pending paid request. */
  selectRequestable(): void {
    this.mode = 'requestable';
    this.revision += 1;
  }

  /** Return selection authority to the paid booking flow. */
  selectPaid(): void {
    this.mode = 'paid';
    this.revision += 1;
  }

  /** Whether a paid request may apply inventory and selection state. */
  canApplyPaidLoad(token: number): boolean {
    return this.mode === 'paid' && token === this.revision;
  }

  /** Whether a paid request is still latest and may finish its loading state. */
  canFinishPaidLoad(token: number): boolean {
    return token === this.revision;
  }
}
